import { createFileRoute, useParams, useNavigate, Link } from '@tanstack/react-router';
import { useEffect, useState, type ChangeEvent, type ReactElement } from 'react';
import { useViewport } from '@/lib/hooks/use-viewport';
import { DAdminShell } from '@/components/organisms/admin-shell';
import { AdminDesktopOnlyNotice } from '@/components/organisms/admin-desktop-only-notice';
import { ReferenceTakeQueuePane } from '@/components/organisms/reference-take-queue-pane';
import { AuthedAudio } from '@/components/molecules/authed-media';
import { ApproveReferenceTakeModal } from '@/components/organisms/approve-reference-take-modal';
import { RejectReferenceTakeModal } from '@/components/organisms/reject-reference-take-modal';
import {
  useReferenceTakeCandidate,
  useReferenceTakeCandidates,
  useApproveReferenceTake,
  useRejectReferenceTake,
  type ReferenceTakeCandidate,
} from '@/lib/queries/reference-take-moderation';
import { emitToast } from '@/lib/toast-bus';
import { ApiError } from '@/lib/api-client';

const PAGE_SIZE = 20;
const NOT_FOUND_STATUS = 404;
const CONFLICT_STATUS = 409;
const PENDING_VERSION = 0;

const SECONDS_PER_MINUTE = 60;

const CONTRIBUTOR_FALLBACK = 'Unknown contributor';

const APPROVE_SUCCESS_MESSAGE = 'Approved · reference published';
const REJECT_SUCCESS_MESSAGE = 'Rejected';
const STALE_MESSAGE = 'This candidate changed since you loaded it. Refresh and try again.';
const ALREADY_REVIEWED_MESSAGE =
  'This candidate has already been reviewed. Refresh and try again.';
const APPROVE_ERROR_MESSAGE = 'Could not approve. Try again.';
const REJECT_ERROR_MESSAGE = 'Could not reject. Try again.';
const QUEUE_PATH = '/admin/reference-takes';

function isStatus(error: unknown, status: number): boolean {
  return error instanceof ApiError && error.status === status;
}

function is404(error: unknown): boolean {
  return isStatus(error, NOT_FOUND_STATUS);
}

function actionErrorMessage(error: unknown, fallback: string): string {
  if (isStatus(error, CONFLICT_STATUS)) return STALE_MESSAGE;
  if (isStatus(error, NOT_FOUND_STATUS)) return ALREADY_REVIEWED_MESSAGE;
  return fallback;
}

function audioPath(referenceTakeId: string): string {
  return `/api/admin/reference-takes/${referenceTakeId}/audio`;
}

function formatDuration(durationSeconds: number | null): string | null {
  if (durationSeconds === null) return null;
  const minutes = Math.floor(durationSeconds / SECONDS_PER_MINUTE);
  const seconds = durationSeconds % SECONDS_PER_MINUTE;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function LoadingShell(): ReactElement {
  return (
    <DAdminShell section="reference-takes" title="Reference takes">
      <div className="p-8 text-[13px] text-[var(--ink-3)]">Loading…</div>
    </DAdminShell>
  );
}

function CandidateNotFoundScreen(): ReactElement {
  return (
    <DAdminShell section="reference-takes" title="Reference takes">
      <div className="p-8 flex flex-col gap-2 items-start">
        <h2 className="text-[18px] font-semibold text-[var(--ink)]">Candidate not found</h2>
        <p className="text-[13px] text-[var(--ink-3)]">
          This contribution may have been reviewed or removed.
        </p>
        <Link to={QUEUE_PATH} className="text-[13px] text-[var(--accent)] hover:underline">
          ← Back to queue
        </Link>
      </div>
    </DAdminShell>
  );
}

function ReferenceTakeReviewCentre({
  candidate,
}: {
  candidate: ReferenceTakeCandidate;
}): ReactElement {
  const duration = formatDuration(candidate.durationSeconds);
  return (
    <div className="flex-1 overflow-auto p-8 flex flex-col gap-6">
      <div>
        <div className="text-[10px] font-mono tracking-[0.18em] uppercase text-[var(--ink-3)]">
          Contributed by
        </div>
        <h2 className="font-display text-[22px] font-semibold mt-1 text-[var(--ink)]">
          {candidate.contributedByDisplayName ?? CONTRIBUTOR_FALLBACK}
        </h2>
        {duration && (
          <div className="text-[12px] text-[var(--ink-3)] mt-1">Duration {duration}</div>
        )}
      </div>
      <div className="max-w-[480px]">
        <div className="text-[11px] font-medium uppercase tracking-wide text-[var(--ink-4)] mb-2">
          Source recording
        </div>
        <AuthedAudio downloadUrl={audioPath(candidate.referenceTakeId)} />
      </div>
      {candidate.notes && (
        <div className="max-w-[480px]">
          <div className="text-[11px] font-medium uppercase tracking-wide text-[var(--ink-4)] mb-1">
            Notes
          </div>
          <p className="whitespace-pre-wrap text-[13px] text-[var(--ink-2)]">{candidate.notes}</p>
        </div>
      )}
    </div>
  );
}

const DECISION_BUTTON_CLASS =
  'h-9 px-3 rounded-[var(--r-sm)] text-[13px] font-medium bg-[var(--bg-soft)] text-[var(--ink)] hover:bg-[var(--bg-strong)] disabled:opacity-50 disabled:cursor-not-allowed';
const TEXTAREA_CLASS =
  'resize-none h-24 p-2 text-[12px] rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-surface)] text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]';

function ReferenceTakeDecisionPanel({
  reason,
  onReasonChange,
  onApprove,
  onReject,
}: {
  reason: string;
  onReasonChange: (reason: string) => void;
  onApprove: () => void;
  onReject: () => void;
}): ReactElement {
  const canReject = reason.trim().length > 0;
  return (
    <aside className="border-l border-[var(--rule)] p-4 flex flex-col gap-3 bg-[var(--bg-surface)]">
      <div className="flex flex-col gap-2">
        <button type="button" className={DECISION_BUTTON_CLASS} onClick={onApprove}>
          Approve
        </button>
        <button
          type="button"
          className={DECISION_BUTTON_CLASS}
          disabled={!canReject}
          onClick={onReject}
        >
          Reject
        </button>
      </div>
      <label className="flex flex-col gap-1 text-[12px] text-[var(--ink-3)]">
        Reason
        <textarea
          aria-label="Reason"
          value={reason}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) => onReasonChange(event.target.value)}
          placeholder="Required for reject"
          className={TEXTAREA_CLASS}
        />
      </label>
    </aside>
  );
}

function AdminReferenceTakeDetailPage(): ReactElement {
  const viewport = useViewport();
  const navigate = useNavigate();
  const { id } = useParams({ from: '/admin/reference-takes/$id' });
  const [reason, setReason] = useState('');
  const [queuePage, setQueuePage] = useState(0);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);

  const detail = useReferenceTakeCandidate(id);
  const queue = useReferenceTakeCandidates({ page: queuePage, size: PAGE_SIZE });
  const approve = useApproveReferenceTake();
  const reject = useRejectReferenceTake();

  useEffect(() => {
    setReason('');
    setIsApproveModalOpen(false);
    setIsRejectModalOpen(false);
  }, [id]);

  if (viewport !== 'desktop') return <AdminDesktopOnlyNotice />;
  if (detail.isError && is404(detail.error)) return <CandidateNotFoundScreen />;
  if (detail.isLoading || !detail.data) return <LoadingShell />;

  const candidate = detail.data;
  const contributorName = candidate.contributedByDisplayName ?? CONTRIBUTOR_FALLBACK;

  const handleApproveConfirm = (): void => {
    approve.mutate(
      { referenceTakeId: id, expectedVersion: PENDING_VERSION },
      {
        onSuccess: () => {
          emitToast({ message: APPROVE_SUCCESS_MESSAGE });
          setIsApproveModalOpen(false);
          void navigate({ to: QUEUE_PATH });
        },
        onError: (error: unknown) => {
          emitToast({ message: actionErrorMessage(error, APPROVE_ERROR_MESSAGE) });
          setIsApproveModalOpen(false);
          void detail.refetch();
        },
      },
    );
  };

  const handleRejectConfirm = (): void => {
    reject.mutate(
      { referenceTakeId: id, expectedVersion: PENDING_VERSION, reason: reason.trim() },
      {
        onSuccess: () => {
          emitToast({ message: REJECT_SUCCESS_MESSAGE });
          setIsRejectModalOpen(false);
          void navigate({ to: QUEUE_PATH });
        },
        onError: (error: unknown) => {
          emitToast({ message: actionErrorMessage(error, REJECT_ERROR_MESSAGE) });
          setIsRejectModalOpen(false);
          void detail.refetch();
        },
      },
    );
  };

  return (
    <DAdminShell section="reference-takes" title="Reference takes">
      <div className="grid grid-cols-[280px_1fr_320px] h-full">
        <ReferenceTakeQueuePane
          page={queue.data}
          isLoading={queue.isLoading}
          currentPage={queuePage}
          onPageChange={setQueuePage}
        />
        <ReferenceTakeReviewCentre candidate={candidate} />
        <ReferenceTakeDecisionPanel
          reason={reason}
          onReasonChange={setReason}
          onApprove={() => setIsApproveModalOpen(true)}
          onReject={() => setIsRejectModalOpen(true)}
        />
      </div>
      {isApproveModalOpen && (
        <ApproveReferenceTakeModal
          contributorName={contributorName}
          isApproving={approve.isPending}
          onConfirm={handleApproveConfirm}
          onCancel={() => setIsApproveModalOpen(false)}
        />
      )}
      {isRejectModalOpen && (
        <RejectReferenceTakeModal
          contributorName={contributorName}
          reason={reason}
          isRejecting={reject.isPending}
          onConfirm={handleRejectConfirm}
          onCancel={() => setIsRejectModalOpen(false)}
        />
      )}
    </DAdminShell>
  );
}

export const Route = createFileRoute('/admin/reference-takes/$id')({
  component: AdminReferenceTakeDetailPage,
});
